import type {
  Category,
  CostCenter,
  DeliveryWindow,
  MenuItem,
  OrderDraft,
  OrderQuote,
  OrderSubmissionOptions,
  SubmittedOrder,
} from '../types/catering';
import { createModuleLogger } from '../utils/logger';

/** Optional category filter for `getMenu`. */
export type MenuFilter = {
  readonly categoryId?: number;
  readonly tsDeliveryDesired?: number;
};

/** The meeting interval `getDeliveryWindows` derives slots within. */
export type DeliveryWindowQuery = {
  readonly meetingStart: Date;
  readonly meetingEnd: Date;
  readonly timeZone?: string;
};

export type OutletResolution = {
  readonly outletId: string;
};

export const MAX_DELIVERY_WINDOWS = 6;

/** Stable failure reasons a `CateringService` can report. */
export type CateringServiceErrorCode = 'not_found' | 'unavailable' | 'not_implemented';

/**
 * Thrown by a `CateringService` implementation for a known failure (e.g. an
 * unknown menu item id). Callers use `code` to choose an error/invalid UI
 * state without parsing `message` text.
 */
export class CateringServiceError extends Error {
  readonly code: CateringServiceErrorCode;
  

  constructor(message: string, code: CateringServiceErrorCode) {
    super(message);
    this.name = 'CateringServiceError';
    this.code = code;
  }
}

const SLOT_INTERVAL_MINUTES = 15;
const log = createModuleLogger('CateringService');

function generateDeliveryWindows(meetingStart: Date, meetingEnd: Date, timeZone?: string) {
  const formatterOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  let formatter: Intl.DateTimeFormat;
  if (!timeZone) {
    formatter = new Intl.DateTimeFormat('en-GB', formatterOptions);
  } else {
    try {
      formatter = new Intl.DateTimeFormat('en-GB', {
        ...formatterOptions,
        timeZone,
      });
    } catch (error) {
      if (!(error instanceof RangeError)) throw error;

      log.warn('Using the browser timezone for an unsupported meeting timezone');
      formatter = new Intl.DateTimeFormat('en-GB', formatterOptions);
    }
  }

  const windows = [];
  for (
    let slotTime = meetingStart.getTime();
    slotTime < meetingEnd.getTime() && windows.length < MAX_DELIVERY_WINDOWS;
    slotTime += SLOT_INTERVAL_MINUTES * 60_000
  ) {
    const deliveryTime = new Date(slotTime);
    windows.push({
      id: deliveryTime.toISOString(),
      label: formatter.format(deliveryTime),
      tsDeliveryDesired: Math.floor(deliveryTime.getTime() / 1000),
    });
  }

  return windows;
}

export async function getDeliveryWindows(query: DeliveryWindowQuery) {
  return generateDeliveryWindows(query.meetingStart, query.meetingEnd, query.timeZone);
}

/**
 * The typed service between the catering UI and its data source. Every method
 * maps 1:1 to a vendor operation `apps/api/src/order-iss` already exposes, so
 * a future API service is pure mapping with no UI changes.
 *
 * Implementations: the explicit mock service under `src/mocks/` and the API
 * service under this app's technical service layer. Outlet resolution stays
 * inside the selected implementation.
 */
export type CateringService = {
  getOutletContext: (room: string) => Promise<OutletResolution>;
  getCategories: () => Promise<Category[]>;
  getMenu: (filter?: MenuFilter) => Promise<MenuItem[]>;
  getMenuItem: (itemId: number) => Promise<MenuItem>;
  getDeliveryWindows: (query: DeliveryWindowQuery) => Promise<DeliveryWindow[]>;
  getCostCenters: () => Promise<CostCenter[]>;
  quoteOrder: (draft: OrderDraft) => Promise<OrderQuote>;
  submitOrder: (draft: OrderDraft, options?: OrderSubmissionOptions) => Promise<SubmittedOrder>;
};
